import { getCurrentCurrency } from "@stores/currency/currencyStore";
import { getCurrentLanguage } from "@stores/language/languageStore";
import { localeOf, type CurrencyCode, type LanguageCode } from "@lib/i18n/languages";

export interface FormatContext {
    language?: LanguageCode;
    currency?: CurrencyCode;
}

/**
 * Backend money is a decimal string ("389.85") in PKR. The currency setting only changes how the
 * figure is labelled — it is not converted (same as the rider and customer apps).
 */
export function formatMoney(
    value: string | number | null | undefined,
    ctx: FormatContext = {}
): string {
    const n = Number(value);
    return new Intl.NumberFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        style: "currency",
        currency: ctx.currency ?? getCurrentCurrency(),
        maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
}

export function formatNumber(
    value: string | number | null | undefined,
    ctx: FormatContext = {}
): string {
    const n = Number(value);
    return new Intl.NumberFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
}

/** A missing or unparseable date shows a dash instead of "Invalid Date" or 1 January 1970. */
function toValidDate(date: string | Date | null | undefined): Date | null {
    if (date == null || date === "") return null;
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(date: string | Date | null | undefined, ctx: FormatContext = {}): string {
    const d = toValidDate(date);
    if (!d) return "—";
    return new Intl.DateTimeFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(d);
}

export function formatDateTime(
    date: string | Date | null | undefined,
    ctx: FormatContext = {}
): string {
    const d = toValidDate(date);
    if (!d) return "—";
    return new Intl.DateTimeFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}

/** Shorthand for chart axes: "26 Sep". */
export function formatDayMonth(date: string | Date | null | undefined, ctx: FormatContext = {}): string {
    const d = toValidDate(date);
    if (!d) return "—";
    return new Intl.DateTimeFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        month: "short",
        day: "numeric",
    }).format(d);
}

/** The signed number with an explicit "+" for gains, e.g. "+12" / "-3". */
export function formatSigned(value: number, ctx: FormatContext = {}): string {
    const s = formatNumber(Math.abs(value), ctx);
    if (value > 0) return `+${s}`;
    if (value < 0) return `-${s}`;
    return s;
}

/** Money with an explicit sign, e.g. "+Rs 50" / "-Rs 25" (zero has none). */
export function formatSignedMoney(value: number, ctx: FormatContext = {}): string {
    const s = formatMoney(Math.abs(value), ctx);
    if (value > 0) return `+${s}`;
    if (value < 0) return `-${s}`;
    return s;
}
